import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonEditGoogleClassroom } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import { partial } from "lodash-es"
import type { Narrative } from "@shared/actions/create-narrative"
import defaultLessonActivity from "./default-lesson-activity"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"

interface Payload {
  prop: "title" | "value" | "maxPoints" | "topicName" | "choices"
  value: string | Array<string> | null
  cardStackId: string
  cardId: string
  originalValue?: string | null
  wordCountChange?: number
}

export default function googleClassroomLessonCardUpdate(payload: Payload): PreparedAction {
  return {
    name: "GOOGLE_CLASSROOM_LESSON_CARD_UPDATE",
    payload: payload,
    patches: patches(payload),
    undoPatches: undoPatches(payload),
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload): Array<PatchModel> {
  let attributePath =
    payload.prop === "topicName" || payload.prop === "choices"
      ? `attributes.extraPostProperties.${payload.prop}`
      : `attributes.${payload.prop}`

  let patch = Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
    id: payload.cardId,
  })
    .set("embedded", attributePath, payload.value)
    .inc("embedded", "attributes._revision", 1).patch

  return [patch]
}

function undoPatches(payload: Payload): Array<PatchModel> {
  let patch = Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
    id: payload.cardId,
  })
    .set("embedded", `attributes.${payload.prop}`, payload.originalValue)
    .inc("embedded", `attributes._revision`, -1).patch

  return [patch]
}

function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative | null {
  let adverbialObjectSpecifier
  let description = ""

  if (payload.prop === "title") {
    adverbialObjectSpecifier = "title"
    description = `Changed GC card title from "${payload.originalValue}" to "${payload.value}"`
  } else if (payload.prop === "value") {
    adverbialObjectSpecifier = "value"
    description = `Edited GC card value.`
  } else if (payload.prop === "maxPoints") {
    adverbialObjectSpecifier = "gc_maxPoints"
    description = `Set the GC points to ${payload.value}`
  } else if (payload.prop === "topicName") {
    adverbialObjectSpecifier = "gc_topic"
    description = `Set the GC topic to ${payload.value}`
  } else if (payload.prop === "choices") {
    adverbialObjectSpecifier = "gc_mc_questions"
    description = `Set the GC multiple choice questions to ${payload.value}`
  }

  let models = defaultLessonModels(payload, findFn, userId)
  let newEvent: LessonEditGoogleClassroom = {
    ...createBlankEvent(),
    root_id: models.planbook?.id ?? null,
    root_type: "planbook",
    date_plan_scheduled_for: models.dateStringForCardStack,
    primary_object_id: payload.cardStackId,
    primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
    primary_verb: "edit",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook?.id),
    primary_object_type: "lesson",
    primary_object_specifier: "google_classroom",

    adverbial_verb: "editing",
    adverbial_object_type: "card",
    adverbial_object_specifier: adverbialObjectSpecifier,
    adverbial_object_id: payload.cardId,
    adverbial_possessive: null,
    adverbial_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
  }

  return {
    title: `Edited Google Classroom ${payload.prop}`,
    titleForAnalytics: "Google Classrooom Lesson Card - Edit",
    description: description,
    context: {
      ...defaultLessonContext(payload, models),
      prop: payload.prop,
    },
    activity: defaultLessonActivity(payload, models, "updated"),
    newAnalyticsEvents: [newEvent],
  }
}
